
import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense } from 'react';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'next-themes';
import { PERMS } from '@/constants/permissions';
import { usePortalAsesorMode } from '@/components/hooks/useCanSeePortalAsesor';
import { navigation } from '@/lib/navigationTree';
import { cn } from "@/lib/utils";
import CompanySwitcher from '@/components/CompanySwitcher';
import { CompanySwitcherActionContext } from '@/components/layout/CompanySwitcherActionContext';
import CommandMenu from '@/components/CommandMenu';
import AIPanel from '@/components/ai/v2/AIPanel';
import { AriaConversationProvider } from '@/components/ai/AriaConversationContext';
import { Toaster } from "sonner";
import { useApp } from '@/components/hooks/useApp';
import { PageErrorBoundary } from '@/components/ErrorBoundary';
import { SkipLink, LiveRegion } from '@/components/utils/Accessibility';
import Sidebar from '@/components/layout/Sidebar';
import { activeNavigationItem, navigationId } from '@/lib/navigation';
import { useNavigationContext } from '@/components/layout/useNavigationContext';
import TopBar from '@/components/layout/TopBar';
import '@/components/layout/aria-home-frame.css';
import { StickyOffsetsProvider } from '@/components/layout/StickyOffsetsProvider';
// Sprint 5.bis.3 — Modo Dios banner. Visibility/exit lógica internas;
// Layout solo decide si aplicar `.client-area` (saturate filter) al wrapper
// del contenido cliente cuando el banner está activo.
import GodBanner from '@/components/layout/GodBanner';
import TrialBanner from '@/components/layout/TrialBanner';
import VerifactuAlarmaBanner from '@/components/layout/VerifactuAlarmaBanner';
import AdvisorShell from '@/components/advisor/AdvisorShell';
import AsesorShell from '@/components/asesor/AsesorShell';
import { shouldRenderAdvisorShell } from '@/lib/advisorRouteScope';
import { useAccountingFirmContext } from '@/components/hooks/useAccountingFirmContext';
import { useLocation, useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { pageVariants, pageTransition } from '@/lib/motion';

const KeyboardShortcuts = lazyWithRetry(() => import('@/components/KeyboardShortcuts'));
const GuidedTour = lazyWithRetry(() => import('@/components/onboarding/GuidedTour'));

// aiTestingUtils - init window utilities
import { useTour } from '@/components/onboarding/GuidedTour';
import { APP_TOUR_ID, appTourSteps } from '@/components/onboarding/appTourSteps';
import OnboardingCelebration from '@/components/onboarding/OnboardingCelebration';
import { useEssentialCelebration } from '@/components/onboarding/useEssentialCelebration';
import { safeStorage } from '@/lib/safeStorage';
import { apiFetch, unwrapOne } from '@/api/apiFetch';
import { qk } from '@/lib/qk';
import { useTenantTheme } from '@/lib/useTenantTheme';
import { useFilteredNavigation, resolveNavModules } from '@/lib/useFilteredNavigation';
import { usePermissions } from '@/components/hooks/usePermissions';
import { useOcrPendientes } from '@/hooks/useOcrPendientes';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';

// Re-export the canonical manifest for routing, ARIA's catalog and invariants.
// Its explicit order and feature-owned entries live in lib/navigationTree.js.
export { navigation };

// Map pageName → i18n key for page titles
// Exportado para `scripts/ui-catalog`: es el nombre que el usuario ve en la miga de
// pan, y por tanto el nombre por el que reconoce la pantalla cuando ARIA se la cita.
export const PAGE_TITLE_KEY = {
  'Home': 'pages.home', 'Facturas': 'pages.facturacion', 'FacturaForm': 'pages.factura_form',
  'FacturaDetalle': 'pages.factura_detail', 'Facturacion': 'pages.facturacion',
  'Configuracion': 'pages.configuracion', 'Asistente': 'pages.asistente',
  'AsistenteAgentic': 'pages.asistente', 'Clientes': 'pages.clientes', 'Contactos': 'pages.contactos',
  'Productos': 'pages.productos', 'Proveedores': 'pages.proveedores',
  'Bancos': 'pages.bancos', 'BancoCuenta': 'pages.banco_cuenta',
  'Finanzas': 'pages.finanzas', 'Gastos': 'pages.gastos',
  'GastosEscaneos': 'pages.gastos_escaneos', 'DietasGastos': 'pages.dietas_gastos',
  'Inventario': 'pages.inventario',
  'InventarioStock': 'pages.inventario_stock', 'InventarioMovimientos': 'pages.inventario_movimientos',
  'InventarioPedidos': 'pages.inventario_pedidos',
  'CRMPipeline': 'pages.crm_pipeline',
  'CRMActividades': 'pages.crm_actividades', 'CRMSecuencias': 'pages.crm_secuencias', 'CRMSegmentacion': 'pages.crm_segmentacion',
  'RRHHEmpleados': 'pages.rrhh_empleados',
  'TimesheetManager': 'pages.timesheet_manager', 'PortalEmpleado': 'pages.portal_empleado',
  'SupervisorPanel': 'pages.supervisor_panel', 'Proyectos': 'pages.proyectos',
  'Projects': 'pages.proyectos',
  'ProjectDetail': 'pages.proyecto_detalle', 'Informes': 'pages.informes',
  'Impuestos': 'pages.impuestos', 'TicketBAIConfig': 'pages.ticketbai_config',
  'TicketBAIStatus': 'pages.ticketbai_status', 
  'ApprovalsDashboard': 'pages.approvals',
  'RolesManagement': 'pages.roles_management', 'HRCommunications': 'pages.hr_communications',
  'EmployeeDetail': 'pages.employee_detail', 'EmployeePortal': 'pages.employee_portal',
  'ExecutiveSummary': 'pages.executive_summary',
  // Accounting hub — único módulo de Contabilidad desde PR14 (legacy retirada).
  'Accounting': 'pages.contabilidad',
  'SkillsManagement': 'pages.skills_management', 'SalarySimulator': 'pages.salary_simulator',
  'Organigrama': 'pages.org_chart', 'Onboarding': 'pages.onboarding',
  'Conciliacion': 'pages.conciliacion', 'VeriFactu': 'pages.verifactu',
  'Nominas': 'pages.nominas', 'ContratosRRHH': 'pages.contratos_rrhh',
  'UserManual': 'pages.user_manual', 'Soporte': 'pages.soporte',
  'ProjectKanban': 'pages.project_kanban',
  'ProyectoInversion': 'pages.proyecto_inversion',
  'ProjectBillingOverview': 'pages.project_billing_overview',
  'Insights': 'pages.insights_hub',
  'InformesRRHH': 'pages.informes_rrhh',
  'ImpuestosHub': 'pages.impuestos',
  // Mounted hubs / sidebar entries that lacked breadcrumbs (audit gap):
  'Dashboard': 'pages.dashboard',
  'FacturasHub': 'pages.facturacion',
  'MyTasks': 'pages.my_tasks',
  'PlantillasFactura': 'pages.plantillas_factura',
  'ProjectsHub': 'pages.proyectos',
  'Recursos': 'pages.recursos',
  'RentabilidadHub': 'pages.rentabilidad',
  'BankAccountsMapping': 'pages.bank_accounts_mapping',
};

/**
 * ¿Esta ruta ES la superficie de ARIA? De aqui depende que NO se monte el boton
 * que abre el panel de ARIA: sobre su propia pantalla abria un SEGUNDO asistente
 * encima del primero (erp#2460).
 *
 * La `i` no es decoracion. React Router empareja rutas SIN distinguir
 * mayusculas, asi que `/Home` sirve la portada igual que `/home`; con el patron
 * sensible, entrar por `/Home` devolvia `false` y salian los dos asistentes.
 * `shell-redesign.spec.ts` lo cazaba desde su ruta `/Home`, y por eso el E2E
 * Smoke llevaba dias en rojo — con el efecto secundario de que un gate rojo deja
 * de mirarse, que es justo lo que su propio workflow advierte.
 *
 * Esta fuera del componente para poder probarla sin montar el Layout entero.
 */
export function esSuperficieDeAria(pathname) {
  // El `(\/|$)` del final tampoco es decoracion: sin el, el patron casaba
  // cualquier ruta que EMPEZARA por una de esas palabras. `/homeopatia` se
  // tomaba por la portada. Hoy no existe esa ruta, pero el patron no deberia
  // depender de que no exista.
  return /^\/(home|asistente-agentic|aria-home)(\/|$)|^\/$/i.test(pathname || '');
}

function LayoutContent({ children, currentPageName }) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [showCompanySwitcher, setShowCompanySwitcher] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [showCommandMenu, setShowCommandMenu] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [celebrateAria, setCelebrateAria] = useState(false);
  // Estable a propósito: `AIPanel` lo mete en las dependencias del callback que
  // pasa a `AssistantMessage`, y ese componente está memoizado comparando
  // `onSourceClick` POR REFERENCIA. Con una arrow inline aquí, la memoización
  // fallaba en cada render y todos los mensajes se repintaban durante el
  // streaming (que actualiza ~60 veces por segundo).
  const handleAiPanelClose = useCallback(() => {
    setAiPanelOpen(false);
    setCelebrateAria(false);
  }, []);
  const { completed: tourCompleted } = useTour(APP_TOUR_ID);
  const [showTour, setShowTour] = useState(false);
  const [screenReaderAnnouncement, setScreenReaderAnnouncement] = useState('');

  // Using AppContext instead of local queries
  const { user, company: currentCompany, switchCompany } = useApp();
  const { hasPermission } = usePermissions();
  const navigationContext = useNavigationContext();

  // Momento "wow" al completar el onboarding esencial: celebración a pantalla
  // completa que ofrece el tour guiado y abre ARIA proactivamente. Vive aquí
  // (no en PrimerosPasos.jsx) porque Layout ya es el único dueño de `showTour`
  // y `aiPanelOpen` — así los CTAs llaman a los setters existentes sin inventar
  // un puente de contexto entre la página y el chrome.
  //
  // La REGLA (detectar la transición sin celebrar de más) vive en
  // `useEssentialCelebration`: tiene dos guardas sutiles que ya fallaron una vez
  // cada una y aquí dentro no había forma de fijarlas con un test. Comparte
  // queryKey —y por tanto caché— con el `useOnboardingStatus()` de
  // PrimerosPasos, así que no añade una petición de red extra.
  const { celebrating: showCelebration, dismiss: dismissCelebration } =
    useEssentialCelebration(currentCompany?.id);

  // Contador de la "Bandeja de documentos" en el menú: documentos OCR pendientes de
  // revisar. Mismo hook (y misma caché) que la página, con el filtro en el servidor
  // (L-02b §3.3): antes contaba sobre los 100 primeros escaneos.
  const { data: ocrPendientes } = useOcrPendientes(currentCompany?.id, { enabled: hasPermission(PERMS.OCR_REVIEW) });
  // (b3) · EL AVISO DE PENDIENTES VERI*FACTU, visible desde cualquier pantalla.
  //
  // Sostiene la frase del 1.d —«mantiene visible un aviso con el número de registros
  // pendientes»— que exige el art. 16.4. Antes de esto, lo único que había era una
  // tarjeta DENTRO de `Facturas → VeriFactu → Estado`: un aviso al que hay que ir no
  // avisa, y además contaba solo los reservados —un rechazado en cola contaba cero—.
  //
  // 🔴 El número llega YA CALCULADO del servidor. No se deriva aquí de los recuentos por
  // estado, y no se podría: separar un rechazado recuperable de uno que no lo es exige
  // el catálogo de errores, que el ERP no tiene. Calcularlo aquí sería una segunda copia
  // del criterio, y dos copias divergen en cuanto alguien toca una.
  //
  // ⚠️ `refetchInterval` y no solo `staleTime`: el caso que hay que cubrir es un rechazo
  // que llega DESPUÉS de emitir, sin que el usuario navegue a ningún sitio. Sin refresco
  // periódico el aviso se quedaría en el número de hace media hora — y un aviso obsoleto
  // es peor que ninguno, porque parece vigente.
  const { data: verifactuStats } = useQuery({
    queryKey: qk.fiscal.verifactuAviso(currentCompany?.id),
    queryFn: async () => unwrapOne(await apiFetch('/api/verifactu-records/stats')),
    enabled: !!currentCompany?.id,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
  });
  const navBadges = useMemo(
    () => ({
      BandejaDocumentos: ocrPendientes?.count ?? 0,
      // 🔴 El OBJETO, no el número. El aviso tiene que verse con el grupo 1 a cero si hay
      // algo en los otros dos, y entonces pinta un símbolo en vez de un «0» — que es lo que
      // un número no sabe expresar. `leerBadge` en `Sidebar.jsx` lo deshace.
      Facturas: verifactuStats ?? 0,
    }),
    [ocrPendientes, verifactuStats]
  );
  // Sprint 5.bis.3 — el filter `.client-area` debe sincronizar visibilidad
  // con el banner. Misma condición que `GodBanner` para evitar drift.
  const { isAsesor, isLoading: isLoadingFirm } = useAccountingFirmContext();
  // Modo de acceso al Portal: 'firm' (despacho) | 'group' (grupo propio) | null.
  // El ecosistema (AsesorShell) se monta en AMBOS modos, no solo para asesores.
  const portalMode = usePortalAsesorMode();
  const { pathname, search } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const prefersReducedMotion = useReducedMotion();

  // Scroll restoration: BrowserRouter (not the data router) doesn't reset scroll
  // on navigation, so pages opened lower down kept the previous scroll offset.
  // Reset to top on pathname change (ignore ?query/tab changes).
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);


  // Señal de "onboarding recién terminado" (NeedsSetupGate.jsx, PrimerosPasosPage.jsx):
  // navegan con ?tour=start. Se consume una vez y se limpia de la URL — mismo
  // patrón que el ?tab=openbanking de Bancos.jsx.
  useEffect(() => {
    if (searchParams.get('tour') === 'start' && !tourCompleted) {
      setShowTour(true);
      const params = new URLSearchParams(searchParams);
      params.delete('tour');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, tourCompleted, setSearchParams]);

  // Panel de ARIA: la barra de navegación global se oculta automáticamente y se
  // revela al hacer hover en el canto izquierdo → el chat/histórico gana espacio.
  //
  // La raíz va EXPLÍCITA (`/$`): `/` monta exactamente la misma pantalla que
  // `/home` (FirmHomeGate → HomeRoute → AriaHome), y sin ella el mismo panel se
  // pintaba distinto según la URL por la que entraras — `<main>` con padding en
  // vez de `p-0`, y el sidebar sin auto-ocultar. `/` sola, no prefijo: `^\/`
  // casaría con toda la aplicación.
  const isAriaPage = esSuperficieDeAria(pathname);
  const [navReveal, setNavReveal] = useState(false);
  const navHideTimer = useRef(null);
  const revealNav = () => { if (navHideTimer.current) clearTimeout(navHideTimer.current); setNavReveal(true); };
  const scheduleHideNav = () => { if (navHideTimer.current) clearTimeout(navHideTimer.current); navHideTimer.current = setTimeout(() => setNavReveal(false), 160); };
  const inGodMode =
    !isLoadingFirm &&
    isAsesor &&
    !!currentCompany?.id &&
    !pathname.startsWith('/portal-asesor') &&
    !pathname.startsWith('/mis-empresas');

  // AD-2a — Layout Dispatcher (Modo Asesor). Decisión pura en `advisorRouteScope`
  // (testeada): flag `ADVISOR_MODE` (⚠️ gate de seguridad) ∧ god_mode ∧ ruta del
  // workspace contable/fiscal.
  const shouldUseAdvisorShell = shouldRenderAdvisorShell({ search, inGodMode, pathname });

  // Ecosistema de despacho: en `/portal-asesor/*` el chrome es el `AsesorShell`
  // (cabecera de despacho + tabbar), NO el rail de módulos de empresa. Es el
  // nivel "firma" del Modo Asesor; entrar en una empresa-cliente (god_mode) saca
  // de aquí. Las rutas inmersivas (presentando / batch result) van a pantalla
  // completa, fuera del shell.
  const isFirmEcosystem =
    !isLoadingFirm &&
    portalMode !== null &&
    (pathname.startsWith('/portal-asesor') || pathname.startsWith('/mis-empresas')) &&
    !/^\/(portal-asesor|mis-empresas)\/(presentando|batch)\b/.test(pathname);

  // Apply tenant theme to :root via <style id="tenant-theme">.
  useTenantTheme();

  // Filter the nav by the tenant's module whitelist (Fase 3 — acceso universal).
  // La personalización del menú es BLANDA y se resuelve SOLO desde
  // `theme.modules` (null/undefined = mostrar todo). El plan ya no interviene en
  // el menú — gobierna únicamente cuotas/billing. Ninguna página se bloquea:
  // ocultarla del menú no impide alcanzarla por URL. Ver `resolveNavModules`.
  const filteredNavigation = useFilteredNavigation(
    navigation,
    resolveNavModules(currentCompany?.theme?.modules),
  );

  // Initialize window utilities with verix instance
  useEffect(() => {
      }, []);
  // Automatic toast notifications removed — only user-initiated actions show toasts

  // Atajos globales del shell: las secuencias `G`/`N` y `⌘/`, que el panel de
  // ayuda anunciaba desde el principio sin que existiera manejador alguno.
  useKeyboardShortcuts({ onToggleAi: () => setAiPanelOpen((v) => !v) });

  // Cambios sin guardar: avisa al cerrar la pestaña y confirma antes de que un
  // enlace del menu saque al usuario de un formulario a medias.
  const { guardDialog } = useUnsavedChangesGuard();

  // `?` abre el propio panel de ayuda. Vive aparte del hook porque su efecto no
  // es navegar ni alternar nada del shell, sino abrir un diálogo cuyo estado es
  // de este componente.
  useEffect(() => {
    const handleGlobalKeys = (e) => {
      // Don't trigger in inputs/textareas
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      if (e.key === '?' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setShowKeyboardShortcuts(true);
      }
    };
    document.addEventListener('keydown', handleGlobalKeys);
    return () => document.removeEventListener('keydown', handleGlobalKeys);
  }, []);

  // Tour solo se lanza manualmente (#32 — no autostart)

  // El drawer móvil se cierra al navegar. Los items del menú ya lo hacen en su
  // onClick, pero no las navegaciones que salen de otro sitio (⌘K, atajos,
  // enlaces dentro de la propia barra) — sin esto el panel quedaba abierto
  // sobre la página nueva.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  // Build breadcrumb path from navigation structure.
  //
  // `currentKey` cae al `nameKey` de la entrada del menú cuando la página no está en
  // PAGE_TITLE_KEY, así que es la fuente buena para el título: pasarle a TopBar
  // `PAGE_TITLE_KEY[currentPageName]` a secas se saltaba ese fallback y la miga acababa
  // pintando el pageName CRUDO (`/datos` decía "DataImport").
  const breadcrumbPath = React.useMemo(() => {
    const allNavGroups = navigation;
    const match = activeNavigationItem(allNavGroups, { pathname, search }, navigationContext, currentPageName);
    if (match) {
      const parent = allNavGroups.find(group => group.subItems?.some(sub => navigationId(sub) === navigationId(match)));
      return { parentKey: parent?.nameKey || null, currentKey: match.nameKeyFor ? match.nameKeyFor(navigationContext) : match.nameKey };
    }
    // Special child pages (FacturaForm → Facturas, EmployeeDetail → RRHHEmpleados, etc.)
    const childPageParents = {
      'FacturaForm': 'Facturas', 'FacturaDetalle': 'Facturas',
      'BancoCuenta': 'Bancos', 'EmployeeDetail': 'RRHHEmpleados',
      'ProjectDetail': 'Projects',
      'PayrollPage': 'Nominas', 'PlantillasFactura': 'Facturas',
    };
    if (childPageParents[currentPageName]) {
      const parentPage = childPageParents[currentPageName];
      for (const group of allNavGroups) {
        if (group.pageName === parentPage) {
          return { parentKey: null, grandparentKey: null, parentPageName: parentPage, parentTitleKey: PAGE_TITLE_KEY[parentPage] || group.nameKey, currentKey: PAGE_TITLE_KEY[currentPageName] };
        }
        if (group.subItems) {
          const parentMatch = group.subItems.find(sub => sub.pageName === parentPage);
          if (parentMatch) {
            return { parentKey: group.nameKey, parentPageName: parentPage, parentTitleKey: PAGE_TITLE_KEY[parentPage] || parentMatch.nameKey, currentKey: PAGE_TITLE_KEY[currentPageName] };
          }
        }
      }
    }
    return { parentKey: null, currentKey: PAGE_TITLE_KEY[currentPageName] };
  }, [currentPageName, pathname, search, navigationContext]);

  // Anuncio de cambio de página para lector de pantalla.
  //
  // En una SPA la navegación no recarga el documento, así que un lector de
  // pantalla no anuncia nada al cambiar de ruta: el usuario pulsa un item del
  // menú y no recibe confirmación de dónde ha aterrizado. `<LiveRegion>` lleva
  // montada desde su día uno (abajo, con `screenReaderAnnouncement`), pero
  // **nadie llamaba nunca al setter**: la región recibía siempre '' y no
  // anunciaba jamás.
  //
  // Va DESPUÉS de `breadcrumbPath` a propósito: el array de dependencias se
  // evalúa durante el render, así que colocarlo arriba —junto al efecto de
  // scroll, que es donde encaja por tema— reventaría por TDZ.
  //
  // La fuente del título es `breadcrumbPath.currentKey` y no
  // `PAGE_TITLE_KEY[currentPageName]`: la primera cae al `nameKey` de la entrada
  // del menú cuando la página no está en el mapa, que es justo el fallback que
  // evita anunciar el pageName crudo ("DataImport" en vez de "Datos").
  const isFirstRouteRef = useRef(true);
  useEffect(() => {
    // No se anuncia el PRIMER render del Layout, donde el lector ya está leyendo
    // el documento recién cargado y un anuncio encima solo pisa.
    //
    // Ojo con lo que esto NO garantiza: el arranque encadena redirecciones
    // internas (`/` → gate → `/home`, o `/mis-empresas` si no hay empresa
    // activa), así que ese primer render se consume ahí y la pantalla en la que
    // el usuario aterriza SÍ se anuncia. Medido: entrando en `/home` se anuncia
    // "ARIA. Página cargada". Es un resultado razonable —quien llega recibe el
    // nombre de donde está—, pero no es "la carga inicial no se anuncia".
    if (isFirstRouteRef.current) {
      isFirstRouteRef.current = false;
      return;
    }
    const key = breadcrumbPath?.currentKey;
    if (!key) return;
    setScreenReaderAnnouncement(t('common.page_changed', { page: t(key) }));
  }, [pathname, breadcrumbPath, t]);

  const handleLogout = () => {
    apiFetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    safeStorage.removeItem('verix_user');
    safeStorage.removeItem('verix_company');
    window.location.href = `${import.meta.env.BASE_URL}login`;
  };

  // D7-04 — `hardReset` (= `queryClient.clear()` en el contexto) en vez del
  // `invalidateQueries()` por defecto. Al cambiar de empresa, invalidar sirve el
  // dato viejo mientras refetchea: durante ese frame la pantalla muestra cifras
  // de OTRA empresa. Preferimos dato frío —y su flash de carga— antes que dato
  // ajeno: es un cambio de tenant, no un refresco.
  //
  // Además hay 10 claves `['asesor', …]` sin `company?.id`, contra la regla de
  // `CLAUDE.md` ("SIEMPRE `company?.id` en el queryKey"): para ésas, invalidar
  // ni siquiera basta, porque la clave del cliente nuevo COLISIONA con la del
  // anterior. Ver el issue enlazado en `useApp.tsx`.
  //
  // Devuelve la promesa: `CompanySwitcher` la espera para su toast y su mutex.
  const handleCompanyChange = (company) => switchCompany(company.id, { hardReset: true });

  const alignedAriaHome = /^\/(home|aria-home)(\/|$)|^\/$/i.test(pathname || '') && !isFirmEcosystem && !shouldUseAdvisorShell;

  return (
    <CompanySwitcherActionContext.Provider value={setShowCompanySwitcher}>
    <div className="min-h-screen spatial-field transition-colors duration-300">
      {/* Skip to content — accessibility */}
      <SkipLink targetId="main-content">
        {t('common.skip_to_content', 'Ir al contenido principal')}
      </SkipLink>

      {/* Mobile menu backdrop */}
      {mobileMenuOpen && (
        <div
          aria-hidden="true"
          /* `z-overlay` va por encima del TopBar (z-50, que debe quedar atenuado
             bajo el velo) y por debajo del drawer (`z-modal`). */
          className="fixed inset-0 bg-black/50 backdrop-blur-[2px] z-overlay lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar (extracted component) - Ahora siempre montado */}
      <Sidebar
        navigation={filteredNavigation}
        badges={navBadges}
        currentPageName={currentPageName}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        expandedMenus={expandedMenus}
        setExpandedMenus={setExpandedMenus}
        currentCompany={currentCompany}
        setShowCompanySwitcher={setShowCompanySwitcher}
        user={user}
        onCmdK={() => setShowCommandMenu(true)}
        floating={false}
        revealed={navReveal}
        onHoverEnter={revealNav}
        onHoverLeave={scheduleHideNav}
      />

      <div
        className={cn(
          "lg:pr-3", alignedAriaHome ? "aria-home-layout transition-[margin-left] duration-200 motion-reduce:transition-none" : "transition-all duration-300",
          collapsed ? "lg:ml-[5.5rem]" : "lg:ml-[17.5rem]"
        )}
      >
        {isFirmEcosystem ? (
          /* Ecosistema de despacho: el `AsesorShell` (cabecera + tabbar) ES la
             navegación a nivel firma. Ahora dentro del área de layout principal
             para respetar el Sidebar global. */
          <AsesorShell
            user={user}
            onLogout={handleLogout}
            setMobileMenuOpen={setMobileMenuOpen}
            onCmdK={() => setShowCommandMenu(true)}
            aiPanelOpen={aiPanelOpen}
            onToggleAIPanel={() => setAiPanelOpen((v) => !v)}
            onSwitchCompany={() => setShowCompanySwitcher(true)}
          >
            {children}
          </AsesorShell>
        ) : shouldUseAdvisorShell ? (
          /* AD-2a — Modo Asesor: chrome propio (GodModeBar + TopBar).
             Ahora también dentro del layout para respetar el Sidebar global. */
          <AdvisorShell
            user={user}
            currentCompany={currentCompany}
            currentPageName={currentPageName}
            breadcrumbPath={breadcrumbPath}
            pageTitleKey={breadcrumbPath.currentKey}
            setMobileMenuOpen={setMobileMenuOpen}
            setShowCommandMenu={setShowCommandMenu}
            setShowKeyboardShortcuts={setShowKeyboardShortcuts}
            onStartTour={() => setShowTour(true)}
            onLogout={handleLogout}
            aiPanelOpen={aiPanelOpen}
            onToggleAIPanel={() => setAiPanelOpen((v) => !v)}
            onSwitchCompany={() => setShowCompanySwitcher(true)}
          >
            {children}
          </AdvisorShell>
        ) : (
          <StickyOffsetsProvider>
            {/* Aviso de prueba gratuita (30 días en el alta pública, 90 si el
                correo estaba en la lista de espera). Self-gates por
                trial_ends_at; solo aviso, sin bloqueo. */}
            <TrialBanner />
            {/* Sprint 5.bis.3 — Modo Dios banner. Encima del TopBar (sticky z-40
                > TopBar z-30). Self-gates por isAsesor + currentCompany + pathname. */}
            <GodBanner />
            {/* F5 · alarma de integridad No VERI*FACTU (F2). Self-gates por el modo de la
                empresa; sin botón de cerrar. */}
            <VerifactuAlarmaBanner />
            {/* TopBar (extracted component) */}
            <TopBar
              avisoStats={verifactuStats ?? null}
              currentPageName={currentPageName}
              breadcrumbPath={breadcrumbPath}
              pageTitleKey={breadcrumbPath.currentKey}
              user={user}
              setMobileMenuOpen={setMobileMenuOpen}
              setShowCommandMenu={setShowCommandMenu}
              setShowKeyboardShortcuts={setShowKeyboardShortcuts}
              onStartTour={() => setShowTour(true)}
              onLogout={handleLogout}
              aiPanelOpen={aiPanelOpen}
              onToggleAIPanel={() => setAiPanelOpen((v) => !v)}
              /* FASE 4 Sub-PR 4.3 — switcher empresa accesible desde topbar
                 (además de desde sidebar, que ya lo tenía). */
              onSwitchCompany={() => setShowCompanySwitcher(true)}
              currentCompany={currentCompany}
              /* Oculta el toggle del AIPanel en la superficie de ARIA — un
                 segundo asistente sobre el panel de ARIA. */
              isAriaPage={isAriaPage}
              alignedWorkspace={alignedAriaHome}
            />

            {/* Page content */}
            <main
              id="main-content"
              className={cn(isAriaPage ? "p-0" : "p-3 sm:p-4 lg:p-6", alignedAriaHome && "aria-home-main", inGodMode && "client-area")}
              tabIndex={-1}
            >
              <PageErrorBoundary>
                {/* Route transition: opacity-only fade (no transform → keeps
                    sticky headers and h-[calc] canvas layouts intact). Skipped
                    on ARIA canvas surfaces and when the user prefers reduced
                    motion.
                    Keyed by the FIRST path segment (not the full pathname): the
                    children tree includes Outlet-based shells (e.g. AccountingHub
                    at /accounting/* with ~25 nested lens routes) whose providers
                    and local state must survive navigation between their nested
                    routes. Keying by full pathname would remount the whole shell
                    on every lens click, defeating the Outlet pattern and losing
                    state. First-segment key animates page↔page but not the nested
                    navigation inside a shell (and ignores ?tab=).
                    Solo fade-in, sin AnimatePresence: con `mode="wait"` la copia
                    que salía seguía pintando `children`, y `<Routes>` lee la
                    location NUEVA — la página destino se montaba dos veces. La
                    primera consumía los deep-links (`/gastos?id=`, `?payslipId=`…)
                    y limpiaba la URL; la segunda, la que queda, ya no los veía. */}
                {isAriaPage || prefersReducedMotion ? (
                  children
                ) : (
                  <motion.div
                    key={pathname.split('/')[1] || 'root'}
                    variants={pageVariants}
                    initial="initial"
                    animate="animate"
                    transition={pageTransition}
                  >
                    {children}
                  </motion.div>
                )}
              </PageErrorBoundary>
            </main>
          </StickyOffsetsProvider>
        )}
      </div>

      {/* Company Switcher Modal */}
      <CompanySwitcher
        open={showCompanySwitcher}
        onOpenChange={setShowCompanySwitcher}
        currentCompany={currentCompany}
        onCompanyChange={handleCompanyChange}
      />
      
      {/* AIPanel v2 — opt-in lateral panel toggled from the topbar.
          ADR-0004: replaces the floating AIAssistant widget. The old
          AIAssistant.jsx file stays on disk as dead code until the
          v2 endpoints (/api/ai/chat, /api/ai/suggestions) ship in PR5. */}
      <AIPanel
        open={aiPanelOpen}
        onClose={handleAiPanelClose}
        activeKey={currentPageName}
        celebrateSetup={celebrateAria}
      />
      <CommandMenu navigation={filteredNavigation} navigationContext={navigationContext} open={showCommandMenu} onOpenChange={setShowCommandMenu} />
      <Suspense fallback={null}>
        <KeyboardShortcuts open={showKeyboardShortcuts} onOpenChange={setShowKeyboardShortcuts} />
      </Suspense>
      <Toaster
        position="bottom-right"
        closeButton
        toastOptions={{ closeButtonAriaLabel: t('notifications.dismiss_toast') }}
        richColors
        theme={theme === 'dark' ? 'dark' : 'light'}
      />

      {/* Guided tour for first-time users */}
      {showTour && (
        <Suspense fallback={null}>
          <GuidedTour
            steps={appTourSteps}
            tourId={APP_TOUR_ID}
            onComplete={() => setShowTour(false)}
            onSkip={() => setShowTour(false)}
          />
        </Suspense>
      )}

      {/* Momento "wow" post-onboarding esencial — ver comentario junto a
          `showCelebration` más arriba. */}
      <OnboardingCelebration
        open={showCelebration}
        companyName={currentCompany?.name}
        onClose={dismissCelebration}
        onStartTour={() => { dismissCelebration(); setShowTour(true); }}
        /* "Hablar con ARIA" abre el AIPanel lateral… salvo en la superficie de
           ARIA, donde el asistente ya está a pantalla completa DETRÁS de esta
           celebración: allí el panel sería un segundo chat sobre el primero, que
           es justo lo que el toggle de la TopBar dejó de hacer. Cerrar la
           celebración ya deja al usuario delante de ARIA. */
        onTalkToAria={() => {
          dismissCelebration();
          if (isAriaPage) return;
          setAiPanelOpen(true);
          setCelebrateAria(true);
        }}
      />

      {/* Global screen reader live region for dynamic announcements */}
      <LiveRegion message={screenReaderAnnouncement} />
      {guardDialog}
    </div>
    </CompanySwitcherActionContext.Provider>
  );
}

// `AppProvider` ya NO se monta aquí: vive en `pages/index.jsx`, por encima de
// `FirstCompanyGate`. Ese gate decide si el ERP se monta siquiera, y para eso
// necesita saber si hay empresa — con el provider aquí dentro, `useApp()` le
// devolvía el fallback (`loading: true`, `user: null`) y dejaba pasar SIEMPRE.
// Mismo fallo que tuvo `/bienvenida` (esqueleto eterno): un componente fuera del
// provider llamando a `useApp`.
//
// Subirlo, en vez de montar un segundo provider, evita que cada carga de página
// pida `/auth/me` dos veces. `Layout` se monta en un único sitio, así que el
// árbol es el mismo.
export default function Layout(props) {
  return (
    <AriaConversationProvider>
      <LayoutContent {...props} />
    </AriaConversationProvider>
  );
}
